import { useCallback, useEffect, useRef } from 'react';

import { toast } from '@/components/ui/toast';
import { useTokenPairStore } from '@/features/tokenPairs';
import type { RecoveredTradePayload, UserTrade } from '@/features/wallet';
import {
    useSpeedBackend,
    userTradeToRecoveredPayload,
} from '@/features/wallet';

import { TERMINAL_STATUS_TO_CLOSE_REASON } from '../services/tradeSnapshot';
import { useTradingStore } from '../store';
import { isActionableActiveTrade } from '../utils/activeTrades';

import { useTradeCloseVisuals } from './useTradeCloseVisuals';

const RECONCILE_RETRIES = 3;
const RECONCILE_BACKOFF_MS = [1000, 2000, 4000];

function pickCanonicalTrade(
    existing: UserTrade,
    candidate: UserTrade,
): UserTrade {
    // Prefer Open over Pending when duplicate hashes appear.
    if (existing.status !== 'Open' && candidate.status === 'Open') {
        return candidate;
    }
    if (existing.status === 'Open' && candidate.status !== 'Open') {
        return existing;
    }

    // Otherwise keep the newest payload for this trade hash.
    const existingTs = new Date(existing.createdAt).getTime();
    const candidateTs = new Date(candidate.createdAt).getTime();

    if (Number.isNaN(existingTs) || Number.isNaN(candidateTs)) {
        return candidate;
    }

    return candidateTs >= existingTs ? candidate : existing;
}

function normalizeActiveTrades(trades: UserTrade[]): UserTrade[] {
    const byHash = new Map<string, UserTrade>();

    for (const trade of trades.filter(isActionableActiveTrade)) {
        const existing = byHash.get(trade.tradeHash);
        if (!existing) {
            byHash.set(trade.tradeHash, trade);
            continue;
        }
        byHash.set(trade.tradeHash, pickCanonicalTrade(existing, trade));
    }

    return [...byHash.values()];
}

function syncSelectedPair(pairSymbol: string): void {
    const normalizedPair = pairSymbol.trim().toLowerCase();
    if (!normalizedPair) return;

    const tokenPairStore = useTokenPairStore.getState();
    if (tokenPairStore.selectedPair !== normalizedPair) {
        tokenPairStore.setSelectedPair(normalizedPair);
    }
}

interface UseTradeReconciliationParams {
    hydrateFromRemoteTrade: (payload: RecoveredTradePayload) => void;
    clearHydratedTrade: () => void;
}

interface UseTradeReconciliationReturn {
    resetReconciliation: () => void;
}

/**
 * Handles startup reconciliation: restores or clears active trade state from
 * the server when auth + network are ready. Writes
 * resumeUnverified directly to the trading store.
 *
 * Retries with exponential backoff on transient failures so network blips
 * don't leave balance/trade state in a wrong state.
 */
export function useTradeReconciliation({
    hydrateFromRemoteTrade,
    clearHydratedTrade,
}: UseTradeReconciliationParams): UseTradeReconciliationReturn {
    const isRealMode = useTradingStore((s) => s.runtimeMode === 'real');
    const token = useTradingStore((s) => s.token);
    const networkId = useTokenPairStore((s) => s.networkId);
    // trades history moved to factions.backend's speed facet (read-only, V1).
    const backend = useSpeedBackend();
    const { fireCloseVisuals } = useTradeCloseVisuals();

    const reconcileFailureToastShownRef = useRef(false);

    const resetReconciliation = useCallback(() => {
        useTradingStore.getState().resetResumeFlags();
    }, []);

    /**
     * Fire close visuals using current store state (replaces the old
     * ref-based pattern). Safe to call from async code.
     */
    const fireCloseVisualsFromStore = useCallback(
        (
            closeReason: Parameters<typeof fireCloseVisuals>[0]['closeReason'],
        ) => {
            const s = useTradingStore.getState();
            if (
                s.execEntryPrice === null ||
                !s.execPositionType ||
                s.execPositionAmount === null ||
                !s.tradeHash
            )
                return;
            fireCloseVisuals({
                entry: s.execEntryPrice,
                exitPrice: s.currentValue,
                posType: s.execPositionType,
                posAmount: s.execPositionAmount,
                leverage: s.execLeverage ?? 1,
                closeReason,
                tradeHash: s.tradeHash,
            });
        },
        [fireCloseVisuals],
    );

    // --- Startup reconciliation ---
    useEffect(() => {
        const networkReady = networkId !== null && networkId !== undefined;
        if (!isRealMode || !token || !backend || !networkReady) {
            return;
        }

        const api = backend;
        const controller = new AbortController();
        let cancelled = false;

        const storeSnap = useTradingStore.getState();
        const hadTradeHash = storeSnap.tradeHash;
        const hadOpenTxHash = storeSnap.openTxHash;

        const store = useTradingStore.getState;

        async function runOpenTradesFetch(attempt: number): Promise<void> {
            if (cancelled || controller.signal.aborted) {
                return;
            }
            try {
                // Resting is in this set even though a resting order is not a
                // position: an empty answer here RESETS everything, so leaving
                // it out would wipe a live limit order off the rail on the next
                // token refresh.
                const openTrades = await api.fetchUserTrades({
                    networkId,
                    status: 'Open,Pending,Resting',
                    limit: 10,
                    signal: controller.signal,
                });
                const normalizedOpenTrades = normalizeActiveTrades(openTrades);
                reconcileFailureToastShownRef.current = false;
                if (cancelled) return;
                if (normalizedOpenTrades.length === 0) {
                    store().setResumeUnverified(false);
                    const hadPersistedTrade = !!(hadTradeHash || hadOpenTxHash);
                    if (hadPersistedTrade) {
                        try {
                            const terminalTrades = await api.fetchUserTrades({
                                networkId,
                                status: 'Closed,TakeProfit,StopLoss,Liquidated,Failed',
                                limit: 20,
                                signal: controller.signal,
                            });
                            if (cancelled) return;
                            const match = terminalTrades.find(
                                (t) =>
                                    t.tradeHash === hadTradeHash ||
                                    (hadOpenTxHash &&
                                        t.openTxHash === hadOpenTxHash),
                            );
                            if (match) {
                                const closeReason =
                                    TERMINAL_STATUS_TO_CLOSE_REASON[
                                        match.status
                                    ];
                                if (closeReason) {
                                    fireCloseVisualsFromStore(closeReason);
                                }
                            }
                            clearHydratedTrade();
                        } catch {
                            if (!cancelled) {
                                console.warn(
                                    '[Trading] Terminal trades fetch failed; leaving state unchanged to avoid hiding an active position.',
                                );
                            }
                        }
                    } else {
                        clearHydratedTrade();
                    }
                } else {
                    // One position per coin, several coins at once — so a list
                    // longer than one is the normal shape now, not the conflict
                    // it used to be treated as. Every trade is hydrated into its
                    // own slot; ONE of them is additionally charted, because the
                    // rail follows the chart and something has to be on it.
                    //
                    // Which one: the trade this client was already tracking, so
                    // a reload puts you back on the market you were watching.
                    // Failing that the newest, since the list is newest-first.
                    // A resting order has no fill, so it has no entry price
                    // and no margin to recover a position from — the payload
                    // builder throws on exactly that. It reaches the store
                    // through the active-trades list instead
                    // (useSyncActiveTradesToStore), which speaks `resting`.
                    // Its presence here still counts: it is why this branch ran
                    // rather than the reset above.
                    const fillable = normalizedOpenTrades.filter(
                        (t) => t.status !== 'Resting',
                    );
                    for (const trade of fillable) {
                        try {
                            hydrateFromRemoteTrade(
                                userTradeToRecoveredPayload(trade),
                            );
                        } catch {
                            // an unhydratable row must not cost us the others —
                            // the primary below re-raises if it is the one that
                            // failed.
                            console.warn(
                                '[Trading] Skipping unhydratable active trade',
                                trade.tradeHash,
                            );
                        }
                    }

                    const activeTrade =
                        normalizedOpenTrades.find(
                            (t) =>
                                (hadTradeHash &&
                                    t.tradeHash === hadTradeHash) ||
                                (hadOpenTxHash &&
                                    t.openTxHash === hadOpenTxHash),
                        ) ??
                        fillable[0] ??
                        normalizedOpenTrades[0];
                    store().setResumeUnverified(false);

                    // Charting it is the only extra step: the trade itself was
                    // already hydrated in the loop above. The localStorage
                    // fallback that used to live here is gone — it stamped the
                    // CHARTED coin onto the position it saved, so it could only
                    // ever tell us a market, never reliably the right one.
                    //
                    // The pair comes from `pairBase` directly rather than from a
                    // recovered payload: a resting row cannot produce one, and
                    // the market is the one thing it does know.
                    syncSelectedPair(
                        `${activeTrade.pairBase.toLowerCase()}-usd`,
                    );
                }
            } catch (err) {
                if (cancelled || controller.signal.aborted) return;
                const isAbort =
                    err instanceof Error && err.name === 'AbortError';
                if (isAbort) return;
                const nextAttempt = attempt + 1;
                if (nextAttempt < RECONCILE_RETRIES) {
                    const delayMs = RECONCILE_BACKOFF_MS[attempt] ?? 4000;
                    await new Promise<void>((resolve) =>
                        setTimeout(resolve, delayMs),
                    );
                    await runOpenTradesFetch(nextAttempt);
                } else {
                    const s = store();
                    const currentHasTradeIdentifiers = Boolean(
                        s.tradeHash || s.openTxHash,
                    );
                    const currentTradeIsVisible =
                        s.executionStatus === 'pending' ||
                        s.executionStatus === 'open' ||
                        currentHasTradeIdentifiers;
                    const shouldLockForSafety = Boolean(
                        !currentTradeIsVisible &&
                        (hadTradeHash || hadOpenTxHash),
                    );
                    store().setResumeUnverified(shouldLockForSafety);
                    if (!reconcileFailureToastShownRef.current) {
                        reconcileFailureToastShownRef.current = true;
                        if (shouldLockForSafety) {
                            toast.error(
                                'Unable to verify open trades from server. Trading is temporarily disabled to protect your balance.',
                                { duration: 12_000 },
                            );
                        }
                    }
                    console.error(
                        '[Trading] Startup reconciliation failed after',
                        RECONCILE_RETRIES,
                        'attempts. Not clearing trade state to avoid hiding an active position.',
                        err,
                    );
                }
            }
        }

        runOpenTradesFetch(0);

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [
        isRealMode,
        token,
        networkId,
        backend,
        fireCloseVisualsFromStore,
        hydrateFromRemoteTrade,
        clearHydratedTrade,
    ]);

    return { resetReconciliation };
}
